import React from "react";
import { AlertOctagon, RefreshCw } from "lucide-react";

/**
 * Global error boundary — catches React render-time errors and shows an
 * on-brand fallback UI with a Reload action + link to the support route.
 * Stays mounted at the root of App.js, ABOVE everything (incl. router).
 */
const FALLBACK_COPY = {
  hr: {
    eyebrow: "Greška",
    title: "Nešto je pošlo po krivu",
    body: "Stranica je naišla na neočekivanu pogrešku. Naš tim je obaviješten. Pokušajte ponovno učitati stranicu — ako se nastavi, javite nam se.",
    reload: "Ponovno učitaj",
    contact: "Kontaktirajte nas",
  },
  en: {
    eyebrow: "Error",
    title: "Something went wrong",
    body: "The page hit an unexpected error. Our team has been notified. Try reloading — if it persists, reach out and we'll help.",
    reload: "Reload",
    contact: "Contact us",
  },
};

function pickLocale() {
  try {
    const path = window.location.pathname;
    const m = path.match(/^\/(hr|en|de|fr)(\/|$)/);
    if (m) return m[1];
    return localStorage.getItem("i18nextLng") || "hr";
  } catch { return "hr"; }
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    const locale = pickLocale();
    const t = FALLBACK_COPY[locale] || FALLBACK_COPY.hr;
    const contactPath = locale === "hr" ? "/contact" : `/${locale}/contact`;
    return (
      <div className="min-h-screen flex items-center justify-center bg-tz-cream p-6">
        <div data-testid="error-boundary-card" className="max-w-lg w-full rounded-2xl bg-white border border-tz-cream-warm shadow-2xl p-8 sm:p-10">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-primary">
            <AlertOctagon className="h-4 w-4" strokeWidth={1.5} />
            {t.eyebrow}
          </div>
          <h1 className="mt-3 font-serif text-3xl sm:text-4xl text-tz-espresso leading-tight">{t.title}</h1>
          <p className="mt-4 text-sm sm:text-base text-tz-espresso/80 leading-relaxed">{t.body}</p>
          {this.state.error?.message && (
            <pre className="mt-4 text-xs font-mono bg-tz-cream-warm/40 rounded-lg p-3 text-tz-espresso/60 overflow-x-auto">
              {String(this.state.error.message).slice(0, 240)}
            </pre>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              data-testid="error-reload-btn"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary text-white px-4 py-2.5 text-sm font-medium shadow-interactive hover:bg-primary/90 transition-colors"
            >
              <RefreshCw className="h-4 w-4" strokeWidth={1.5} /> {t.reload}
            </button>
            <a
              href={contactPath}
              data-testid="error-contact-link"
              className="inline-flex items-center gap-2 rounded-lg border border-tz-espresso/15 px-4 py-2.5 text-sm font-medium text-tz-espresso hover:bg-tz-cream-warm/40 transition-colors"
            >
              {t.contact}
            </a>
          </div>
        </div>
      </div>
    );
  }
}
